import _debounce from 'lodash/debounce';
import React, { useLayoutEffect, useState } from 'react';

/**
 * Provided an array of media queries and an associated
 * array of values, it will match a media query to a value
 * on resize and return it.
 * If no queries match, it will return a default value.
 */
export default function useMedia(queries, values, defaultValue) {
  const match = React.useCallback(
    () => values[queries.findIndex(q => matchMedia(q).matches)] || defaultValue,
    [defaultValue, queries, values]
  );
  const [value, set] = React.useState(match);

  React.useEffect(() => {
    const handler = _debounce(() => set(match), 150);

    window.addEventListener('resize', handler);

    return () => window.removeEventListener('resize', handler);
  }, [match]);

  return value;
}

export function useWidthQuery(ref, { maxWidth, minWidth, defaultValue }) {
  const [matches, setMatches] = useState(defaultValue ?? false);

  useLayoutEffect(() => {
    const node = ref?.current;

    if (!node) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) {
        return;
      }

      const width = entry.contentRect.width;
      const maxWidthCheck = maxWidth ? width <= maxWidth : true;
      const minWidthCheck = minWidth ? width >= minWidth : true;

      setMatches(minWidthCheck && maxWidthCheck);
    });

    observer.observe(node);

    return () => observer.unobserve(node);
  }, [ref, maxWidth, minWidth]);

  return matches;
}
